import type { RouteWithGeometry, Waypoint } from '$lib/components/map/types';
import type {
	BspAutoTrackingName,
	DistanceUnitTracker,
	FloatingAdBasePayload,
	PartnerBasePayload,
	PartnerBoxmapBasePayload,
	PartnerCardBasePayload,
} from '$lib/services/tracking/googleAnalytics/events';

import type { SearchParamsType } from '$lib/services/urls/searchParams';
import type { ItiAddress } from '$routes/(map)/routes/types';

import { tripOptionsCarName } from '$lib/components/itinerary/tripOptions';
import { track } from '$lib/services/tracking/googleAnalytics';
import { booleanToString, filterDefaultCarId, trafficModeIsWithTraffic } from '$lib/services/tracking/googleAnalytics/tracking';

export interface PartnerCardTrackingParams {
	searchParams: SearchParamsType;
	arrivalAddress?: ItiAddress;
	departureAddress?: ItiAddress;
	route?: RouteWithGeometry;
	advertiser: string;
	advertisers: string;
	waypoints?: Waypoint[];
}
interface PartnerItineraryTrackingParams {
	searchParams: SearchParamsType;
	arrivalAddress?: ItiAddress;
	departureAddress?: ItiAddress;
	route?: RouteWithGeometry;
	advertiser?: string;
	waypoints?: Waypoint[];
	routeIndex?: number;
}

export function buildPartnerTrackingData(
	params: PartnerItineraryTrackingParams,
): PartnerBasePayload {
	const itiResultTrackingData = params.route !== undefined
		? {
				country_departure: params.departureAddress?.country?.name ?? '',
				city_departure: params.departureAddress?.city ?? '',
				zip_departure: params.departureAddress?.zipCode ?? '',
				country_arrival: params.arrivalAddress?.country?.name ?? '',
				city_arrival: params.arrivalAddress?.city ?? '',
				zip_arrival: params.arrivalAddress?.zipCode ?? '',
				latitude_departure: params.waypoints?.[0].location.lat.toString(),
				longitude_departure: params.waypoints?.[0].location.lng.toString(),
				latitude_arrival: params.waypoints?.at(-1)?.location.lat.toString(),
				longitude_arrival: params.waypoints?.at(-1)?.location.lng.toString(),
				unit: (params.searchParams.distanceSystem === 'IMPERIAL' ? 'mi' : 'm') as DistanceUnitTracker,
				distance: params.route?.routeDistance?.value?.toString(),
				cost: params.route?.totalCost?.amount?.toString(),
				toll_cost: params.route?.costs?.tolls?.amount?.toString(),
				user_currency: params.route?.totalCost.currency,
				travel_mode: params.searchParams.travelMode,
				drive_with_electric_vehicle: booleanToString(!!params.route?.isEv),
				drive_with_caravan: booleanToString(!!params.searchParams?.withCaravan),
				drive_with_traffic: booleanToString(trafficModeIsWithTraffic(params.searchParams?.traffic)),
				route_preference: params.searchParams.tripConstraint,
				itinerary_type: params.route?.types.join('|'),
				itinerary_exploration: typeof params.routeIndex === 'number' ? String(params.routeIndex) : '',
			}
		: {};

	return {
		add_advertiser: params?.advertiser,
		car_name: params.searchParams.car ? tripOptionsCarName.serialize(params.searchParams.car) : undefined,
		car_id: filterDefaultCarId(params.searchParams.car),
		...itiResultTrackingData,
	};
}

export function buildPartnerCardTrackingData(
	params: PartnerCardTrackingParams,
): PartnerCardBasePayload {
	return {
		add_type: 'adcards',
		add_advertisers_loaded: params.advertisers,
		...buildPartnerTrackingData(params),
	};
}

export function buildPartnerBoxmapTrackingData(
	params: PartnerItineraryTrackingParams,
): PartnerBoxmapBasePayload {
	return {
		add_type: 'boxmap',
		...buildPartnerTrackingData(params),
	};
}

export function trackPartnerCardLoaded(params: PartnerCardTrackingParams) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'display',
			...buildPartnerCardTrackingData(params),
		},
	});
}

export function trackPartnerCardClicked(params: PartnerCardTrackingParams) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'click',
			...buildPartnerCardTrackingData(params),
		},
	});
}

export function trackPartnerBoxmapLoaded(params: PartnerItineraryTrackingParams) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'display',
			...buildPartnerBoxmapTrackingData(params),
		},
	});
}

export function trackPartnerBoxmapClick(params: PartnerItineraryTrackingParams) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'click',
			...buildPartnerBoxmapTrackingData(params),
		},
	});
}

export function buildFloatingAdTrackingData(
	params: PartnerItineraryTrackingParams,
): FloatingAdBasePayload {
	return {
		add_type: 'floating_ad',
		...buildPartnerTrackingData(params),
	};
}

export function trackFloatingAdCtaClick(
	params: PartnerItineraryTrackingParams & { advertiser: BspAutoTrackingName },
) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'click',
			...buildFloatingAdTrackingData(params),
			add_advertiser: params.advertiser,
			add_type: params.advertiser === 'BSP_AUTO' ? 'floating_ad' : 'adcards',
		},
	});
}

export function trackFloatingAdDisplay(
	params: PartnerItineraryTrackingParams & { advertiser: BspAutoTrackingName },
) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'display',
			...buildFloatingAdTrackingData(params),
			add_advertiser: params.advertiser,
			add_type: params.advertiser === 'BSP_AUTO' ? 'floating_ad' : 'adcards',
		},
	});
}

export function trackAdsTextLineCtaClick(
	params: PartnerItineraryTrackingParams & { advertiser: string },
) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'click',
			add_type: 'option_line',
			...buildPartnerTrackingData(params),
		},
	});
}

export function trackAdsTextLineDisplay(
	params: PartnerItineraryTrackingParams & { advertiser: string },
) {
	track({
		action: 'add_interactions',
		payload: {
			action: 'display',
			add_type: 'option_line',
			...buildPartnerTrackingData(params),
		},
	});
}
